import React, { useRef } from 'react';
import I18n from 'i18n-js';
import { UnstyledButton } from '@/components/ui/Buttons';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/Table';
import { TCITooltip } from '@/components/ui/Tooltip';
import cn from '@/utils/cn';
import { scoreColorClass, tagDescription } from './actions';
import { SelectedScore, StudentScores, Tag } from './types';
import useAngledHeaderHeight from './useAngledHeaderHeight';

const CELL_WIDTH = 'tw-w-[3.75rem] tw-min-w-[3.75rem]';
// AIDEV-NOTE: The table's own top border scrolls away under the sticky header, so header cells draw it instead.
const HEADER_TOP_EDGE = 'tw-shadow-[inset_0_1px_0_theme(colors.light-grey)]';
const STICKY_COLUMN = 'tw-sticky tw-left-0 tw-w-48 tw-min-w-48 tw-bg-white tw-text-very-dark-grey tw-text-left '
  + 'tw-border-0 tw-border-r-2 tw-border-solid tw-border-dark-grey';

// AIDEV-NOTE: Geometry is coupled. Labels shrink to their text up to 8rem at -60deg, and useAngledHeaderHeight sizes
// the header to the tallest one. The lane divider is skewed -30deg to run parallel to the labels, so at mid-height a
// lane's center sits tan(30deg) * height/2 (28.87cqh) right of the column's; labels center there, on its centerline.
// Table cells can't be size containers, hence the LANE_BOX wrapper. Lanes are ~52px across: three leading-4 lines fit.
// The last lane leans tan(30deg) * height past the table's right edge, so the wrapper reserves that much trailing room.
const TAN_30 = Math.tan(Math.PI / 6);
const LANE_BOX = 'tw-absolute tw-inset-0 [container-type:size]';
const ANGLED_CODE = 'tw-absolute tw-top-1/2 tw-left-[calc(50%+28.87cqh)] tw-w-max tw-max-w-32 '
  + 'tw-translate-x-[-50%] tw-translate-y-[-50%] tw-rotate-[-60deg] tw-text-left tw-leading-4 tw-break-words';
const LANE_DIVIDER = 'tw-absolute tw-inset-0 tw-origin-bottom-left tw-skew-x-[-30deg] '
  + 'tw-border-0 tw-border-l tw-border-solid tw-border-light-grey';

const BreakableCode = ({ code }: { code: string }) => {
  const segments = code.split('.');

  return (
    <>
      {segments.map((segment, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>
          {segment}
          {index < segments.length - 1 && <>.<wbr /></>}
        </React.Fragment>
      ))}
    </>
  );
};

// AIDEV-NOTE: <wbr> splits the computed accessible name ("6. 1. 8"), hence aria-label on the th.
const StandardHeaderCell = ({ tag }: { tag: Tag }) => (
  <TableCell
    header
    aria-label={tag.alias}
    className={cn(CELL_WIDTH, HEADER_TOP_EDGE, 'tw-relative tw-p-0 tw-text-tci-purple')}
    scope="col"
  >
    <span className={LANE_BOX}>
      <span aria-hidden className={LANE_DIVIDER} />
      <TCITooltip>
        <TCITooltip.Trigger asChild>
          <span data-angled-label className={ANGLED_CODE}>
            <BreakableCode code={tag.alias} />
          </span>
        </TCITooltip.Trigger>
        <TCITooltip.Content className="tw-max-w-[250px] tw-p-2 tw-text-[11px]">
          <b>{tag.alias}</b>
          {' '}
          {tagDescription(tag)}
        </TCITooltip.Content>
      </TCITooltip>
    </span>
  </TableCell>
);

interface ScoreTableProps {
  students: StudentScores[];
  tags: Tag[];
  onSelectScore: (selected: SelectedScore) => void;
}

const ScoreTable = ({ students, tags, onSelectScore }: ScoreTableProps) => {
  const headerRef = useRef<HTMLTableSectionElement>(null);
  const headerHeight = useAngledHeaderHeight(headerRef, tags.map(tag => tag.alias).join('|'));

  return (
    <div className="tw-w-fit" style={{ paddingRight: headerHeight && Math.ceil(headerHeight * TAN_30) }}>
      <Table
        className={cn(
          'tw-border-separate tw-border-spacing-0 tw-text-sm',
          students.length > 0 && 'tw-border tw-border-t-0 tw-border-r-0 tw-border-solid tw-border-light-grey',
        )}
      >
        <TableHeader ref={headerRef} className="tw-sticky tw-top-0 tw-z-[2] tw-bg-white">
          <TableRow style={{ height: headerHeight }}>
            <TableCell
              header
              className={cn(
                STICKY_COLUMN,
                HEADER_TOP_EDGE,
                'tw-z-[3] tw-box-border tw-h-[5.5rem] tw-align-bottom tw-px-3.5 tw-py-3',
              )}
              scope="col"
            >
              {I18n.t('students')}
            </TableCell>
            {tags.map(tag => <StandardHeaderCell key={tag.id} tag={tag} />)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {students.map(student => (
            <TableRow key={student.id}>
              <TableCell
                header
                className={cn(STICKY_COLUMN, 'tw-z-[1] tw-h-[3.75rem] tw-px-3.5 tw-align-middle tw-py-0 tw-font-bold')}
                scope="row"
              >
                {student.name}
              </TableCell>
              {student.scores.map((score, index) => (
                <TableCell
              // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={cn(
                    CELL_WIDTH,
                    'tw-p-0 tw-text-center tw-align-middle tw-text-tci-purple',
                    // The table has no right border so the last header lane stays open; body rows draw it here.
                    'last:tw-border-0 last:tw-border-r last:tw-border-solid last:tw-border-light-grey',
                    scoreColorClass(score),
                  )}
                >
                  {score === null ? '-' : (
                    <UnstyledButton
                      className="tw-w-full tw-h-[3.75rem] tw-text-tci-purple"
                      onClick={() => onSelectScore({ score, student, tag: tags[index] })}
                    >
                      {`${score}%`}
                    </UnstyledButton>
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ScoreTable;
